/**
 * V2 Header Bar Section Field Configuration
 *
 * V2 equivalent of headerSectionConfig.ts.
 * Maps V2 StepNode.content.header paths.
 *
 * V2 path changes from V1:
 * - navigationBar.back → content.header.back
 * - navigationBar.skip → content.header.skip
 * - navigationBar.back.content.l10n → content.header.back.content.translations
 * - navigationBar.skip.content.l10n → content.header.skip.content.translations
 * - navigationBar.pageIndicator → content.header.progressIndicator
 *
 * Sprint: schema-v2-migration, Sprint 3
 */

import type { FieldConfig, ButtonStyleGroup } from '../types/editorFieldTypes';
import { createTextStyleFields, remapTextFieldsForV2, createV2IconFields } from './atomicFields';
import { createIconEditorComponent } from '../components/shared/styleFields/styleGroupEditorFactories';

/**
 * V2 Back Button field configuration
 *
 * Path: content.header.back
 * Translations: content.header.back.content.translations
 * Styles: content.header.back.content.appearance (V2 uses appearance not styles)
 */
export const V2_BACK_BUTTON_CONFIG: FieldConfig = {
  path: 'content.header.back',
  label: 'Back Button',
  placeholder: 'Back',
  type: 'text',
  l10nPath: 'content.header.back.content',
  defaultValue: '❮',
  disableBehavior: 'null',
  guideKey: 'screenedit-header-back-button',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.header.back.content')),
};

/**
 * V2 Skip Button field configuration
 *
 * Path: content.header.skip
 * Translations: content.header.skip.content.translations
 */
export const V2_SKIP_BUTTON_CONFIG: FieldConfig = {
  path: 'content.header.skip',
  label: 'Skip Button',
  placeholder: 'SKIP',
  type: 'text',
  l10nPath: 'content.header.skip.content',
  defaultValue: 'SKIP',
  disableBehavior: 'null',
  guideKey: 'screenedit-header-skip-button',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.header.skip.content')),
};

/**
 * V2 Progress Indicator field configuration
 *
 * V2 uses a flat ProgressIndicator with all settings at one level.
 * `kind` controls which fields are active in the editor.
 */
export const V2_PROGRESS_INDICATOR_CONFIG: FieldConfig = {
  path: 'content.header.progressIndicator',
  label: 'Page Indicator',
  type: 'pageIndicator',
  min: 0,
  max: 100,
  step: 1,
  defaultValue: 50,
  disableBehavior: 'null',
  guideKey: 'screenedit-header-progress-indicator-kind',

  // Indicator kind path (now inside progressIndicator)
  indicatorKindPath: 'content.header.progressIndicator.kind',

  // Dashes configuration path (same object, flat)
  dashesConfigPath: 'content.header.progressIndicator',

  // Line type style fields (v2.13.0: flat activeColor/trackColor)
  styleFields: [
    {
      path: 'content.header.progressIndicator.activeColor',
      label: 'page-indicator-color',
      type: 'color',
      defaultValue: '#ffc201',
      isNullable: false,
      guideKey: 'screenedit-header-progress-active-color',
    },
    {
      path: 'content.header.progressIndicator.trackColor',
      label: 'page-indicator-track',
      type: 'color',
      defaultValue: '#ffffff',
      isNullable: false,
      guideKey: 'screenedit-header-progress-track-color',
    },
  ],

  // Dashes type style fields (v2.13.0: shared activeColor/trackColor)
  dashesStyleFields: [
    {
      path: 'content.header.progressIndicator.activeColor',
      label: 'filled color',
      type: 'color',
      defaultValue: '#12777f',
      isNullable: false,
    },
    {
      path: 'content.header.progressIndicator.trackColor',
      label: 'not filled color',
      type: 'color',
      defaultValue: '#f2f2f2',
      isNullable: false,
    },
  ],

  // Title config (universal, available for any kind)
  titleConfig: {
    path: 'content.header.progressIndicator.title',
    label: 'title',
    type: 'text',
    l10nPath: 'content.header.progressIndicator.title',
    defaultValue: '5/10',
    guideKey: 'screenedit-header-progress-title',
    styleFields: remapTextFieldsForV2(createTextStyleFields('content.header.progressIndicator.title')),
  },
};

// ═══════════════════════════════════════════════════════
// Header Container Appearance (content.header.appearance + content.header.insets)
// ═══════════════════════════════════════════════════════

export const V2_HEADER_HEIGHT_CONFIG: FieldConfig = {
  path: 'content.header.appearance.height',
  label: 'Height',
  type: 'number',
  min: 20,
  max: 200,
  step: 1,
  defaultValue: 44,
  guideKey: 'screenedit-header-appearance-height',
};

export const V2_HEADER_BG_COLOR_CONFIG: FieldConfig = {
  path: 'content.header.appearance.backgroundColor',
  label: 'Background Color',
  type: 'color',
  defaultValue: undefined,
  isNullable: true,
  guideKey: 'screenedit-header-appearance-bg-color',
};

export const V2_HEADER_OPACITY_CONFIG: FieldConfig = {
  path: 'content.header.appearance.opacity',
  label: 'Opacity',
  type: 'number',
  min: 0,
  max: 1,
  step: 0.05,
  defaultValue: 1,
  guideKey: 'screenedit-header-appearance-opacity',
};

/** Path for PaddingElement — header container insets */
export const V2_HEADER_INSETS_PATH = 'content.header.insets';

// ═══════════════════════════════════════════════════════
// Progress Indicator — Icon sub-section (reuses button icon pattern)
// (content.header.progressIndicator.icon/iconPlacement/iconInsets)
// ═══════════════════════════════════════════════════════

/** Icon fields for ProgressIndicator — same pattern as TapTarget button icons */
export const V2_PROGRESS_ICON_FIELDS = createV2IconFields('content.header.progressIndicator');

/** Icon style group — renders as "Icon" accordion in ButtonStyleSections.
 *  Uses customComponent (same as action bar buttons) instead of legacy iconUpload path. */
export const V2_PROGRESS_ICON_STYLE_GROUP: ButtonStyleGroup = {
  id: 'icon',
  title: 'Icon',
  defaultExpanded: false,
  fields: V2_PROGRESS_ICON_FIELDS,
  objectPath: 'content.header.progressIndicator.icon',
  customComponent: createIconEditorComponent('content.header.progressIndicator'),
  paddingConfig: {
    basePath: 'content.header.progressIndicator.iconInsets',
    label: 'Icon Padding',
  },
};

/** Content alignment — alignment of the icon+title container (NOT part of icon group) */
export const V2_PROGRESS_CONTENT_ALIGNMENT_CONFIG: FieldConfig = {
  path: 'content.header.progressIndicator.contentAlignment',
  label: 'Content Alignment',
  type: 'select',
  defaultValue: 'center',
  guideKey: 'screenedit-header-progress-content-alignment',
  options: [
    { value: 'start', label: 'Start' },
    { value: 'center', label: 'Center' },
    { value: 'end', label: 'End' },
  ],
};

// ═══════════════════════════════════════════════════════
// Progress Layout (content.header.progressLayout)
// ═══════════════════════════════════════════════════════

export const V2_PROGRESS_FULL_WIDTH_CONFIG: FieldConfig = {
  path: 'content.header.progressLayout.fullWidth',
  label: 'Full Width',
  type: 'toggle',
  defaultValue: false,
  guideKey: 'screenedit-header-progress-full-width',
};

export const V2_PROGRESS_RESPECT_BUTTON_CONFIG: FieldConfig = {
  path: 'content.header.progressIndicator.respectButton',
  label: 'Respect Buttons',
  type: 'toggle',
  defaultValue: true,
  guideKey: 'screenedit-header-progress-respect-buttons',
};

export const V2_PROGRESS_V_ALIGN_CONFIG: FieldConfig = {
  path: 'content.header.progressLayout.verticalAlignment',
  label: 'Vertical Alignment',
  type: 'select',
  defaultValue: 'center',
  guideKey: 'screenedit-header-progress-vertical-alignment',
  options: [
    { value: 'top', label: 'Top' },
    { value: 'center', label: 'Center' },
    { value: 'bottom', label: 'Bottom' },
  ],
};

/** Path for PaddingElement — progress indicator insets */
export const V2_PROGRESS_INSETS_PATH = 'content.header.progressLayout.insets';

/**
 * All V2 header bar field configurations in render order
 */
export const V2_HEADER_BAR_FIELD_CONFIGS: FieldConfig[] = [
  V2_BACK_BUTTON_CONFIG,
  V2_SKIP_BUTTON_CONFIG,
  V2_PROGRESS_INDICATOR_CONFIG,
];
